import { styled } from '@mui/material/styles'
import { Box } from '@mui/material'
import {
	colors,
	spacing,
	typography,
	radius,
	shadows,
	transitions,
	focusVisible,
	zIndex,
} from '@/styles/designTokens'

export const StyledCountrySelectWrapper = styled(Box)({
	position: 'relative',
	display: 'flex',
	alignItems: 'stretch',

	// Flag icon: 24x24 per Figma (rendered by the library's iconComponent)
	'& .PhoneInputCountryIcon': {
		width: '24px',
		height: '24px',
		boxShadow: 'none',
		backgroundColor: 'transparent',
		flexShrink: 0,
	},

	'& .PhoneInputCountryIconImg': {
		width: '100%',
		height: '100%',
	},
})

export const StyledTriggerButton = styled('button', {
	shouldForwardProp: (prop) => prop !== '$readOnly',
})<{ $readOnly?: boolean }>(({ $readOnly }) => ({
	display: 'flex',
	alignItems: 'center',
	gap: `${spacing.sm}px`,
	padding: `${spacing.lg}px`,
	margin: 0,
	border: `1px solid ${colors.border}`,
	borderRight: 'none',
	borderRadius: `${radius.badge}px 0 0 ${radius.badge}px`,
	backgroundColor: colors.surfaceWhite,
	cursor: $readOnly ? 'default' : 'pointer',

	'&:focus-visible': {
		...focusVisible,
		outlineColor: colors.brand,
	},

	'&:disabled': {
		cursor: 'default',
	},
}))

export const StyledCallingCode = styled('span')({
	color: colors.textSecondary,
	fontFamily: typography.fontFamily,
	fontSize: `${typography.fontSizeLg}px`,
	fontWeight: typography.fontWeightRegular,
	lineHeight: typography.lineHeightLg,
	letterSpacing: typography.letterSpacingWide,
	whiteSpace: 'nowrap',
})

export const StyledArrow = styled('span')({
	display: 'block',
	width: '0.3em',
	height: '0.3em',
	borderBottom: `1px solid ${colors.textSecondary}`,
	borderRight: `1px solid ${colors.textSecondary}`,
	transform: 'rotate(45deg)',
	opacity: 0.6,
})

export const StyledDropdown = styled(Box)({
	position: 'absolute',
	top: '100%',
	left: 0,
	marginTop: `${spacing.xs}px`,
	width: '280px',
	display: 'flex',
	flexDirection: 'column',
	backgroundColor: colors.surfaceWhite,
	border: `1px solid ${colors.border}`,
	borderRadius: `${radius.badge}px`,
	boxShadow: shadows.dropdown,
	zIndex: zIndex.dropdown,
})

export const StyledSearchInput = styled('input')({
	border: 'none',
	borderBottom: `1px solid ${colors.borderSubtle}`,
	outline: 'none',
	backgroundColor: 'transparent',
	padding: `${spacing.md}px ${spacing.lg}px`,
	color: colors.textPrimary,
	fontFamily: typography.fontFamily,
	fontSize: `${typography.fontSizeMd}px`,
	fontWeight: typography.fontWeightRegular,
	lineHeight: typography.lineHeightMd,
	letterSpacing: typography.letterSpacingWide,

	'&::placeholder': {
		color: colors.textSecondary,
	},

	'&:focus-visible': {
		...focusVisible,
		outlineColor: colors.brand,
		outlineOffset: '-2px',
		borderBottomColor: colors.focusBorder,
	},
})

export const StyledOptionsList = styled('ul')({
	listStyle: 'none',
	margin: 0,
	padding: 0,
	maxHeight: '240px',
	overflowY: 'auto',
})

export const StyledOptionItem = styled('li', {
	shouldForwardProp: (prop) => prop !== 'isActive',
})<{ isActive: boolean }>(({ isActive }) => ({
	display: 'flex',
	alignItems: 'center',
	gap: `${spacing.sm}px`,
	padding: `${spacing.md}px ${spacing.lg}px`,
	cursor: 'pointer',
	backgroundColor: isActive ? colors.overlayMedium : 'transparent',
	transition: `background-color ${transitions.fast}`,

	'&:hover': {
		backgroundColor: colors.overlaySubtle,
	},

	'&:focus-visible': {
		...focusVisible,
		outlineColor: colors.brand,
	},
}))

export const StyledOptionLabel = styled('span')({
	flex: 1,
	color: colors.textPrimary,
	fontFamily: typography.fontFamily,
	fontSize: `${typography.fontSizeMd}px`,
	fontWeight: typography.fontWeightRegular,
	lineHeight: typography.lineHeightMd,
	letterSpacing: typography.letterSpacingWide,
	whiteSpace: 'nowrap',
	overflow: 'hidden',
	textOverflow: 'ellipsis',
})

export const StyledOptionCode = styled('span')({
	color: colors.textSecondary,
	fontFamily: typography.fontFamily,
	fontSize: `${typography.fontSizeMd}px`,
	fontWeight: typography.fontWeightRegular,
	lineHeight: typography.lineHeightMd,
	letterSpacing: typography.letterSpacingWide,
	whiteSpace: 'nowrap',
})

export const StyledNoResults = styled(Box)({
	padding: `${spacing.md}px ${spacing.lg}px`,
	color: colors.textSecondary,
	fontFamily: typography.fontFamily,
	fontSize: `${typography.fontSizeMd}px`,
	fontWeight: typography.fontWeightRegular,
	lineHeight: typography.lineHeightMd,
	letterSpacing: typography.letterSpacingWide,
})
